import {UserRole} from 'enums';
import { IServiceNowUser, IUserPreferences } from 'interfaces';
import { IUser, IUserServer } from 'interfaces/IUser';
// Not using index import for services to avoid circular dependency
import {axiosAPI} from './API';

// Singleton for interacting with users
class _UsersService {
    // This gets set by App.tsx when okta reads the stored token
    public oktaUserInfo: {
        role?: UserRole,
        id?: string,
    } = {};
    private getIsApproverPromise: Promise<boolean>;

    public me: IUser;

    public getAndCacheMe(): Promise<IUser> {
        const p = axiosAPI.get<IUserServer>('/users/me')
            .then((response) => {
                const user = this.mapUserFromServer(response.data);
                // Cache the user so that we don't have to load "me" on every page.
                this.me = user;
                return user;
            })

        p.catch((err) => {
            console.error('Error getting users/me: ', err);
        });

        return p;
    }

    public savePreferences(prefs: IUserPreferences): Promise<any> {
        const p = axiosAPI.post<void>('/users/me/preferences', prefs)

        p.catch((err) => {
            console.error('Error saving user preferences: ', err);
        });

        return p;
    }

    public getIsApprover(): Promise<boolean> {
        // Cache the promise so that we only do this call once since this is unlikely to change.
        if (!this.getIsApproverPromise) {
            this.getIsApproverPromise = axiosAPI.get<{isApprover: boolean}>('/isApprover')
                .then((response) => response.data.isApprover)

            this.getIsApproverPromise.catch((err) => {
                console.error('Error getting isApprover: ', err);
            });
        }

        return this.getIsApproverPromise;
    }

    // This is used by the backend to create a user in the users table.
    // This is necessary to make sure the user is there so that
    // we can populate "created_by" or "modified_by" fields.
    public logUser(): Promise<any> {
        const p = axiosAPI.post('/logUser')
        
        p.catch((err) => {
            console.error('Error logging user: ', err);
        });

        return p;
    }

    public getApprovers(): Promise<IUser[]> {
        const p = axiosAPI
            .get<IUserServer[]>('/approvers')
            .then((response) => {
                return response.data.map((user) => this.mapUserFromServer(user));
            });
        p.catch((err) => {
            console.error("Error getting approvers: ", err);
        });
        return p;
    }

    public postApprover(sysId: string): Promise<any> {
        const p = axiosAPI.post<IUserServer>('/approvers/' + sysId)
        p.catch((err) => {
            console.error("Error adding approver: ", err);
        });
        return p;
    }

    public deleteApprover(userId: string): Promise<any> {
        const p = axiosAPI.delete<IUserServer>('/approvers/' + userId)
        p.catch((err) => {
            console.error("Error removing approver: ", err);
        });
        return p;
    }

    public mapServiceNowUserToUser(sNowUser: IServiceNowUser): IUser {
        return {
            createdAt: null,
            createdBy: null,
            email: sNowUser.email,
            isApprover: null,
            modifiedAt: null,
            modifiedBy: null,
            name: sNowUser.name,
            snowId: sNowUser.u_guid,
            timezone: '',
        };
    }

    public mapUserFromServer(u: IUserServer): IUser {
        const user: IUser = {
            ...u,
            createdAt: new Date(u.createdAt),
            modifiedAt: new Date(u.modifiedAt)
        }
        return user;
    }

    public parseOktaUserInfo(authState) {
        let role = UserRole.ViewOnly;
        let id = undefined;

        // We'll read the user info from the claims in the access token
        const claims = authState?.accessToken?.claims
        if (claims) {
            id = claims.cfa_guid;
            const cfaPerms = claims.cfa_perms?.SMARTEQUIP_UPDATER;
            
            if (cfaPerms) {
                if (cfaPerms['ADMIN']) {
                    role = UserRole.Admin;
                } else if (cfaPerms['FULL-USER']) {
                    role = UserRole.FullUser;
                } else if (cfaPerms['LIMITED-USER']) {
                    role = UserRole.LimitedUser;
                } else if (cfaPerms['LOGIN']) {
                    role = UserRole.ViewOnly;
                } else {
                    console.error(`Didn't find a valid role in cfa_perms. authState: `, authState);
                }
            } else {
                console.error(`No cfa_perms map in token. authState: `, authState);
            }
        } else {
            console.error('No claims found in token. authState: ', authState);
        }

        this.oktaUserInfo = {
            role: role,
            id: id
        };
    }

    public reset() {
        this.getIsApproverPromise = undefined;
        this.oktaUserInfo = {};
    }
}

export const UsersService = new _UsersService();